/* ============================================================================
   Tour do professor — DEFINIÇÃO DOS PASSOS
   ============================================================================
   Dados puros: nenhum import, nenhum React, nenhum DOM. É o que permite testar
   a ordem, os alvos e a numeração sem montar o tour (ver filtro.test.js).

   O tour é SOB DEMANDA: só roda quando o professor pede pelo ícone no header.
   Não existe flag de "já viu" — decisão de produto tomada em 03/08/2026 para
   manter o módulo 100% no front, sem endpoint e sem coluna nova. A consequência
   aceita: não há métrica de adesão. Se um dia ela for pedida, o lugar é um
   endpoint de evento, e nada aqui precisa mudar.

   COMO OS ALVOS FORAM ESCOLHIDOS
   No AVA o menu vem do backend (access/role/listRoleMenuGroup) e varia por
   grupo, escola e módulos contratados — não existe "o menu do professor". Então
   nenhum passo pode assumir que seu alvo existe:

   - itens de menu são ancorados por HREF no link que o CSidebarNavItem já
     renderiza (`.c-sidebar-nav-link[href="..."]`). Zero alteração no Sidebar, e
     o cliente que não tem o item simplesmente perde o passo;
   - os botões de alocar e relatório já vêm com id da tabela compartilhada
     (`allocateBtn-{linha}` / `reportBtn-{linha}` em components/Tables/tables.jsx);
   - só o botão "Novo" precisou de âncora nova (`data-tour="tabela-novo"`),
     porque a classe dele não é única na página.

   `verificarNoInicio` separa as duas naturezas de ausência:
   - true  -> alvo que vive no DOM desde sempre (menu). Se não está lá na hora
              de abrir o tour, aquele cliente não tem a seção: o passo é
              descartado ANTES de começar, e a numeração já sai sem ele.
   - false -> alvo que só existe depois de navegar ou que depende de dado (a
              linha 0 da tabela pressupõe o professor ter ao menos uma
              atividade). Esse é resolvido em tempo de execução, esperando o
              alvo aparecer e pulando no timeout.
   ========================================================================== */

/**
 * Os passos, na ordem da jornada: turmas -> criar/localizar -> alocar ->
 * acompanhar resultados.
 *
 * O formato de um passo está documentado em ./index.js, junto das constantes
 * compartilhadas — é o mesmo formato do roteiro do aluno.
 */
const PASSOS = [
  {
    id: "boas-vindas",
    alvo: "body",
    posicao: "center",
    verificarNoInicio: false,
    titulo: "Vamos conhecer a plataforma",
    texto:
      "Em poucos passos, você conhecerá os principais recursos da plataforma. " +
      "Vamos mostrar onde acessar suas turmas, criar atividades, enviá-las aos " +
      "alunos e acompanhar os resultados. Você pode encerrar este tour quando " +
      "quiser e retomá-lo pelo ícone de ajuda no topo da tela."
  },

  /* ---------------------------------------------------------------- turmas -- */
  {
    id: "menu-turmas",
    alvo: '.c-sidebar-nav-link[href="/classroom/me"]',
    posicao: "right",
    verificarNoInicio: true,
    rotaEsperada: "/classroom/me",
    permiteCliqueNoAlvo: true,
    titulo: "Minhas Turmas",
    texto:
      "Aqui estão as turmas pelas quais você é responsável. Em cada uma delas, " +
      "você pode acessar os alunos, acompanhar atividades e gerenciar o " +
      "aprendizado da turma."
  },
  {
    id: "lista-turmas",
    alvo: "#list, .animated.fadeIn",
    posicao: "top",
    verificarNoInicio: false,
    dependeDe: "menu-turmas",
    titulo: "Lista de Turmas",
    texto:
      "Ao abrir uma turma, você terá acesso à lista de alunos, às atividades " +
      "enviadas e às respostas registradas por cada estudante."
  },
  {
    id: "menu-todas-turmas",
    alvo: '.c-sidebar-nav-link[href="/classroom/list"]',
    posicao: "right",
    verificarNoInicio: true,
    rotaEsperada: "/classroom/list",
    permiteCliqueNoAlvo: true,
    titulo: "Todas as Turmas",
    texto:
      "Nesta área, você encontra a listagem completa das turmas e recursos de " +
      "gerenciamento, como a importação de alunos. Clique para visualizar."
  },
  {
    id: "importar-alunos",
    alvo: "#importStudentsBtn-0",
    posicao: "left",
    verificarNoInicio: false,
    dependeDe: "menu-todas-turmas",
    titulo: "Importar Alunos",
    texto:
      "Este botão permite importar vários alunos para a turma de uma só vez, " +
      "utilizando uma planilha. Cada turma possui seu próprio botão de " +
      "importação."
  },

  /* ------------------------------------------------ criar / localizar ------- */
  {
    id: "menu-atividades",
    alvo: '.c-sidebar-nav-link[href="/activity/list"]',
    posicao: "right",
    verificarNoInicio: true,
    rotaEsperada: "/activity/list",
    permiteCliqueNoAlvo: true,
    titulo: "Minhas Atividades",
    texto:
      "Nesta área, você cria, edita, organiza e gerencia as atividades que " +
      "serão disponibilizadas aos alunos. Clique para acessar."
  },
  {
    id: "criar-atividade",
    alvo: '[data-tour="tabela-novo"]',
    posicao: "bottom",
    verificarNoInicio: false,
    dependeDe: "menu-atividades",
    titulo: "Criar uma Atividade",
    texto:
      "Clique neste botão para criar uma nova atividade. Você poderá selecionar " +
      "os conteúdos, adicionar questões e configurar a atividade antes de " +
      "publicá-la."
  },

  /* ---------------------------------------------------------------- alocar -- */
  {
    id: "alocar-atividade",
    alvo: "#allocateBtn-0",
    posicao: "left",
    verificarNoInicio: false,
    dependeDe: "menu-atividades",
    titulo: "Enviar para a Turma",
    texto:
      "Após criar uma atividade, utilize este botão para enviá-la às turmas. " +
      "Você poderá definir quais turmas receberão a atividade e o prazo para " +
      "conclusão."
  },

  /* ----------------------------------------------------------- resultados -- */
  {
    id: "relatorio-atividade",
    alvo: "#reportBtn-0",
    posicao: "left",
    verificarNoInicio: false,
    dependeDe: "menu-atividades",
    titulo: "Resultados da Atividade",
    texto:
      "Depois que os alunos responderem à atividade, você poderá acompanhar o " +
      "desempenho da turma, verificar quem concluiu a atividade e identificar " +
      "os conteúdos que apresentaram maior dificuldade."
  },

  /* ------------------------------------------------------ minhas trilhas ---- */
  {
    id: "menu-minhas-trilhas",
    alvo: '.c-sidebar-nav-link[href="/activity/list?trail=true"]',
    posicao: "right",
    verificarNoInicio: true,
    titulo: "Minhas Trilhas",
    texto:
      "As trilhas organizam o aprendizado em etapas. Os alunos avançam conforme " +
      "concluem cada fase, tornando o processo mais dinâmico e estruturado."
  },

  /* ----------------------------------------------------------- avaliações --- */
  {
    id: "menu-avaliacoes",
    alvo: '.c-sidebar-nav-link[href="/evaluation/list"]',
    posicao: "right",
    verificarNoInicio: true,
    titulo: "Minhas Avaliações",
    texto:
      "Nesta seção, você cria, aplica e acompanha avaliações. Também é possível " +
      "consultar correções, notas e o desempenho dos alunos."
  },

  {
    id: "menu-avaliacoes-adr",
    alvo: '.c-sidebar-nav-link[href="/evaluation/adr/list"]',
    alvoPorTexto: ["adr"],
    posicao: "right",
    verificarNoInicio: true,
    titulo: "Avaliações ADR",
    texto:
      "Aqui você gerencia as avaliações ADR. Nesta área, é possível cadastrar, " +
      "agendar e acompanhar o andamento das avaliações organizadas por " +
      "disciplina e série."
  },

  /* ------------------------------------------- inteligência educacional ----- */
  {
    id: "menu-inteligencia-educacional",
    alvo: '.c-sidebar-nav-link[href="/inteligencia-educacional"]',
    posicao: "right",
    verificarNoInicio: true,
    titulo: "Inteligência Educacional",
    texto:
      "Este painel reúne informações sobre o desempenho e o engajamento dos " +
      "alunos, ajudando você a identificar oportunidades de acompanhamento e " +
      "intervenção pedagógica."
  },

  /* -------------------------------------------------------------- usuários -- */
  {
    id: "menu-usuarios",
    alvo:
      '.c-sidebar-nav-link[href="/userlist"], ' +
      '.c-sidebar-nav-link[href="/Userlist"]',
    posicao: "right",
    verificarNoInicio: true,
    titulo: "Usuários",
    texto:
      "Nesta área, você consulta os usuários cadastrados na plataforma. É " +
      "possível localizar alunos, professores e demais usuários, além de " +
      "visualizar suas informações cadastrais."
  },
  {
    id: "menu-dashboard",
    alvo: '.c-sidebar-nav-link[href="/dashboard"]',
    posicao: "right",
    verificarNoInicio: true,
    titulo: "Painel Inicial",
    texto:
      "O painel inicial apresenta uma visão geral das suas atividades, turmas e " +
      "informações importantes, facilitando o acompanhamento das tarefas do dia."
  },
  {
    id: "fim",
    alvo: "body",
    posicao: "center",
    verificarNoInicio: false,
    titulo: "Tour concluído!",
    texto:
      "Agora você conhece os principais recursos da plataforma para gerenciar " +
      "suas turmas, criar atividades, acompanhar avaliações e monitorar o " +
      "desempenho dos alunos. Sempre que desejar, você poderá revisar este tour " +
      "clicando no ícone de ajuda no topo da tela."
  }
];

export default PASSOS;
